import React, { useState, useRef } from "react";
import { ImageIcon, ArrowUp, X } from "lucide-react";
import { useFileUpload } from "../../hooks/chat/useFileUpload";
import { useImageUploadForTarget } from "../../hooks/chat/useImageUploadForTarget";
import { ProcessedFile } from "../../context/types";

interface ChatInputProps {
  onSendMessage: (message: string, files?: ProcessedFile[]) => void;
  isLoading?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({ onSendMessage, isLoading = false }) => {
  const [message, setMessage] = useState("");
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  const {
    selectedImage,
    imagePreview,
    imageUrl,
    isUploading: fileUploading,
    handleImageSelect,
    processFiles,
    clearFiles
  } = useFileUpload();

  const { uploadImageForTarget, isUploading } = useImageUploadForTarget();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Send all content through the unified chat flow to display in messages
    if ((message.trim() || selectedImage) && !isLoading && !isUploading && !fileUploading) {
      try {
        const processedFiles = await processFiles();
        onSendMessage(message, processedFiles);
        setMessage("");
        clearFiles();
        if (imageInputRef.current) imageInputRef.current.value = "";
      } catch (error) {
        console.error('Failed to process files:', error);
      }
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const removeImage = () => {
    clearFiles();
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleFileClick = (inputRef: React.RefObject<HTMLInputElement>) => {
    inputRef.current?.click();
  };

  return (
    <div className="py-4 px-2 sm:px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full bg-sidebar border border-sidebar-border shadow-sm p-3 rounded-xl"
      >
        {/* File previews */}
        {imagePreview && (
          <div className="relative inline-block">
            <img 
              src={imagePreview} 
              alt="Preview" 
              className="max-w-32 max-h-32 rounded-lg border border-gray-200"
            />
            {fileUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                <div className="text-white text-xs">Uploading...</div>
              </div>
            )}
            {imageUrl && !fileUploading && (
              <div className="absolute -top-1 -left-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            )}
            <button
              type="button"
              onClick={removeImage}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
              aria-label="Remove image"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        
        <div className={`flex items-center w-full relative ${imagePreview ? 'mt-3' : ''}`}>
          <input
            type="file"
            ref={imageInputRef}
            accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            className="hidden"
            onChange={handleImageChange}
          />
          <button
            type="button"
            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex justify-center items-center hover:bg-gray-100 transition-colors mr-1"
            onClick={() => handleFileClick(imageInputRef)}
            aria-label="Add image"
            disabled={isLoading || isUploading || fileUploading}
          >
            <ImageIcon 
              className="w-4 h-4 sm:w-5 sm:h-5 text-[#6B7280]"
              opacity={isLoading || isUploading || fileUploading ? 0.5 : 1}
            />
          </button>
          
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="flex-1 bg-transparent outline-none text-sidebar-foreground px-2 text-sm sm:text-base placeholder:text-muted-foreground"
            placeholder="Message..."
            aria-label="Message input"
            disabled={isLoading || isUploading || fileUploading}
          />
          <button
            type="submit"
            className={`w-8 h-8 sm:w-10 sm:h-10 bg-[#734E85] rounded-full flex justify-center items-center text-white ${
              isLoading || isUploading || fileUploading || (!message.trim() && !selectedImage) 
                ? "opacity-50 cursor-not-allowed" 
                : "hover:bg-[#5F436C] transition-colors"
            }`}
            aria-label="Send message"
            disabled={isLoading || isUploading || fileUploading || (!message.trim() && !selectedImage)}
          >
            <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatInput;
